import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Images, CheckCircle2, Clock, CloudUpload, EyeOff, Trash2, ShieldCheck } from "lucide-react";

const KINDS = ["reflection", "wellbeing_shift", "streak", "quote", "milestone"];
const ACCENTS = ["#7C8A6E", "#B8C4A2", "#D9E0CB", "#EFDDBE", "#F5D6C5", "#C7D0DE"];

const STATUS = {
  published: { key: "soc.wall_status_published", cls: "bg-primary text-primary-foreground" },
  unlisted: { key: "soc.wall_status_unlisted", cls: "bg-secondary text-muted-foreground" },
  draft: { key: "soc.wall_status_draft", cls: "bg-secondary text-muted-foreground" },
};

function StatusPill({ status }) {
  const t = useT();
  const s = STATUS[status] || STATUS.draft;
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${s.cls}`}>{t(s.key)}</span>;
}

export default function StoryWallTab() {
  const t = useT();
  const [moments, setMoments] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [draft, setDraft] = useState({ client_id: "", kind: "reflection", title: "", body: "", metric_label: "", color_accent: ACCENTS[0] });
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [coachShowName, setCoachShowName] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/coach/story-wall/moments"); setMoments(data?.moments || []); }
    catch { setMoments([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = async () => {
    setDraft({ client_id: "", kind: "reflection", title: "", body: "", metric_label: "", color_accent: ACCENTS[0] });
    setCreateOpen(true);
    try {
      const { data } = await api.get("/coach/my-clients");
      const list = (data?.clients || []).map((c) => ({ id: c.client_id || c.id, name: c.name, email: c.email }));
      setClients(list);
      if (list.length) setDraft((d) => ({ ...d, client_id: list[0].id }));
    } catch { setClients([]); }
  };

  const create = async () => {
    if (!draft.client_id || !draft.title.trim() || !draft.body.trim()) { toast.error(t("soc.wall_missing_fields")); return; }
    setSaving(true);
    try {
      await api.post("/coach/story-wall/moments", {
        client_id: draft.client_id, kind: draft.kind, title: draft.title.trim(),
        body: draft.body.trim(), metric_label: draft.metric_label.trim() || undefined, color_accent: draft.color_accent,
      });
      toast.success(t("soc.wall_saved"));
      setCreateOpen(false); await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const setCoachConsent = async (consent, showName) => {
    if (!detail) return;
    setBusy(true);
    try { const { data } = await api.post(`/coach/story-wall/moments/${detail.id}/consent`, { consent, show_name: showName }); setDetail(data); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); setConsentOpen(false); }
  };

  const publish = async () => {
    if (!detail) return;
    setBusy(true);
    try { const { data } = await api.post(`/coach/story-wall/moments/${detail.id}/publish`); setDetail(data); toast.success(t("soc.wall_published_ok")); await load(); }
    catch (err) { toast.error(apiError(err, t("soc.wall_publish_needs_consents"))); }
    finally { setBusy(false); }
  };

  const unpublish = async () => {
    if (!detail) return;
    setBusy(true);
    try { await api.post(`/coach/story-wall/moments/${detail.id}/unpublish`); setDetail(null); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  const archive = async () => {
    if (!detail || !window.confirm(t("soc.wall_archive_confirm"))) return;
    setBusy(true);
    try { await api.delete(`/coach/story-wall/moments/${detail.id}`); setDetail(null); await load(); }
    catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="soc-wall-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{t("soc.wall_sub")}</p>
        <Button className="gap-1.5 rounded-full" onClick={openCreate} data-testid="soc-wall-new">
          <Plus className="h-4 w-4" /> {t("soc.wall_new")}
        </Button>
      </div>

      {moments === null ? (
        <div className="columns-1 gap-3 sm:columns-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="mb-3 h-36 rounded-2xl" />)}</div>
      ) : moments.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Images className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("soc.wall_empty_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("soc.wall_empty_sub")}</p>
        </div>
      ) : (
        <div className="columns-1 gap-3 sm:columns-2">
          {moments.map((m) => (
            <button key={m.id} data-testid={`soc-wall-card-${m.id}`} onClick={() => setDetail(m)} className="mb-3 block w-full break-inside-avoid rounded-2xl p-4 text-left transition-transform hover:-translate-y-0.5" style={{ backgroundColor: m.color_accent || "#EFDDBE" }}>
              <div className="flex items-center justify-between">
                <span className="rounded-full bg-white/50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-foreground">{t(`soc.wall_kind_${m.kind}`)}</span>
                <StatusPill status={m.status} />
              </div>
              {m.metric_label && <p className="mt-2 font-display text-2xl font-semibold text-foreground">{m.metric_label}</p>}
              <p className="mt-1 font-medium text-foreground">{m.title}</p>
              <p className="mt-1 line-clamp-5 text-sm text-foreground/80">{m.body}</p>
              <div className="mt-3 flex gap-2">
                <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold ${m.coach_consent ? "bg-white/80 text-foreground" : "bg-black/10 text-foreground/60"}`}>C</span>
                <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-semibold ${m.client_consent ? "bg-white/80 text-foreground" : "bg-black/10 text-foreground/60"}`}>K</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="soc-wall-create-dialog">
          <DialogHeader>
            <DialogTitle>{t("soc.wall_create_title")}</DialogTitle>
            <DialogDescription>{t("soc.wall_consent_note")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("soc.wall_client")}</label>
              {clients.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("common.none_found")}</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {clients.map((c) => (
                    <button key={c.id} onClick={() => setDraft((d) => ({ ...d, client_id: c.id }))} data-testid={`soc-wall-client-${c.id}`} className={`rounded-full border px-3 py-1.5 text-sm ${draft.client_id === c.id ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>{c.name || c.email}</button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("soc.wall_kind")}</label>
              <div className="flex flex-wrap gap-1.5">
                {KINDS.map((k) => (
                  <button key={k} onClick={() => setDraft((d) => ({ ...d, kind: k }))} className={`rounded-full border px-3 py-1.5 text-sm ${draft.kind === k ? "border-primary bg-primary/10 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>{t(`soc.wall_kind_${k}`)}</button>
                ))}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("soc.wall_title_lbl")}</label>
              <Input value={draft.title} onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} maxLength={140} placeholder={t("soc.wall_title_ph")} data-testid="soc-wall-title" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("soc.wall_body_lbl")}</label>
              <Textarea className="min-h-[90px]" value={draft.body} onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} maxLength={600} placeholder={t("soc.wall_body_ph")} data-testid="soc-wall-body" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("soc.wall_metric_lbl")}</label>
              <Input value={draft.metric_label} onChange={(e) => setDraft((d) => ({ ...d, metric_label: e.target.value }))} maxLength={60} placeholder={t("soc.wall_metric_ph")} data-testid="soc-wall-metric" />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("soc.wall_color_lbl")}</label>
              <div className="flex gap-2">
                {ACCENTS.map((c) => (
                  <button key={c} onClick={() => setDraft((d) => ({ ...d, color_accent: c }))} className={`h-7 w-7 rounded-full border-2 ${draft.color_accent === c ? "border-foreground" : "border-transparent"}`} style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setCreateOpen(false)}>{t("common.cancel")}</Button>
            <Button className="rounded-full" onClick={create} disabled={saving} data-testid="soc-wall-save">{saving ? t("common.saving") : t("soc.wall_save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="soc-wall-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">{detail.title} <StatusPill status={detail.status} /></DialogTitle>
                <DialogDescription>{detail.metric_label || t(`soc.wall_kind_${detail.kind}`)}</DialogDescription>
              </DialogHeader>
              <div className="rounded-xl p-4" style={{ backgroundColor: detail.color_accent || "#EFDDBE" }}>
                <p className="whitespace-pre-wrap text-sm text-foreground/90">{detail.body}</p>
              </div>

              <div className="flex items-center justify-between border-t border-border pt-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{t("soc.wall_coach_consent")}</p>
                  <p className="text-xs text-muted-foreground">{detail.coach_consent ? t("soc.wall_consent_granted") : t("soc.wall_consent_pending")}</p>
                </div>
                <Button size="sm" variant={detail.coach_consent ? "outline" : "default"} className="gap-1.5 rounded-full" onClick={() => { setCoachShowName(detail.coach_show_name); setConsentOpen(true); }} data-testid="soc-wall-coach-consent">
                  <ShieldCheck className="h-4 w-4" /> {detail.coach_consent ? t("soc.wall_review") : t("soc.wall_review_agree")}
                </Button>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-3">
                <div>
                  <p className="text-sm font-medium text-foreground">{t("soc.wall_client_consent")}</p>
                  <p className="text-xs text-muted-foreground">{detail.client_consent ? t("soc.wall_consent_granted") : t("soc.wall_consent_waiting_client")}</p>
                </div>
                {detail.client_consent ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <Clock className="h-5 w-5 text-muted-foreground" />}
              </div>

              <DialogFooter className="flex-col gap-2 sm:flex-col">
                {detail.status === "published" ? (
                  <Button variant="outline" className="w-full gap-1.5 rounded-full" disabled={busy} onClick={unpublish} data-testid="soc-wall-unpublish"><EyeOff className="h-4 w-4" /> {t("soc.wall_unpublish")}</Button>
                ) : (
                  <Button className="w-full gap-1.5 rounded-full" disabled={busy || !detail.coach_consent || !detail.client_consent} onClick={publish} data-testid="soc-wall-publish"><CloudUpload className="h-4 w-4" /> {t("soc.wall_publish")}</Button>
                )}
                <Button variant="ghost" className="w-full gap-1.5 rounded-full text-rose-500 hover:text-rose-600" disabled={busy} onClick={archive} data-testid="soc-wall-archive"><Trash2 className="h-4 w-4" /> {t("soc.wall_archive")}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Coach consent dialog */}
      <Dialog open={consentOpen} onOpenChange={setConsentOpen}>
        <DialogContent data-testid="soc-wall-consent-dialog">
          <DialogHeader>
            <DialogTitle>{t("soc.wall_coach_consent_title")}</DialogTitle>
            <DialogDescription>{t("soc.wall_coach_consent_body")}</DialogDescription>
          </DialogHeader>
          <label className="flex items-center justify-between rounded-xl border border-border p-3">
            <div>
              <p className="text-sm font-medium text-foreground">{t("soc.wall_show_name")}</p>
              <p className="text-xs text-muted-foreground">{t("soc.wall_show_name_sub")}</p>
            </div>
            <Switch checked={coachShowName} onCheckedChange={setCoachShowName} data-testid="soc-wall-show-name" />
          </label>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" disabled={busy} onClick={() => setCoachConsent(false, false)} data-testid="soc-wall-consent-revoke">{t("soc.wall_consent_revoke")}</Button>
            <Button className="rounded-full" disabled={busy} onClick={() => setCoachConsent(true, coachShowName)} data-testid="soc-wall-consent-grant">{t("soc.wall_consent_grant")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
